import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertOctagon, Check, X, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { tpmApi } from '../../services/tpm'
import { formatDateTime } from '../../utils/format'

export default function TpmPending() {
  const { t } = useTranslation()
  const qc = useQueryClient()

  const KIND_LABEL = {
    RecoveryFetch: t('tpm.pending.kinds.recoveryFetch'),
    PcrRebaseline: t('tpm.pending.kinds.pcrRebaseline'),
    Enrollment: t('tpm.pending.kinds.enrollment'),
    RecoveryKeyRotation: t('tpm.pending.kinds.recoveryKeyRotation'),
  }

  const { data: pending = [], isLoading, refetch } = useQuery({
    queryKey: ['tpm', 'pending'],
    queryFn: () => tpmApi.listPending(),
    refetchInterval: 10000,
  })

  const approve = useMutation({
    mutationFn: ({ id, note }) => tpmApi.approve(id, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpm', 'pending'] }),
  })
  const deny = useMutation({
    mutationFn: ({ id, note }) => tpmApi.deny(id, note),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tpm', 'pending'] }),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <AlertOctagon className="w-6 h-6 text-amber-600" />
            {t('tpm.pending.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('tpm.pending.subtitle')}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          {t('tpm.pending.refresh')}
        </button>
      </div>

      {isLoading && <div className="text-gray-400 text-sm">{t('tpm.pending.loading')}</div>}
      {!isLoading && pending.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 text-center text-gray-500">
          {t('tpm.pending.empty')}
        </div>
      )}

      <div className="space-y-3">
        {pending.map((p) => {
          let payload = null
          try { payload = p.payloadJson ? JSON.parse(p.payloadJson) : null } catch { /* ignore */ }
          return (
            <div key={p.id} className="bg-white rounded-lg shadow-sm border border-amber-100 p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-gray-800">
                    {KIND_LABEL[p.kind] || p.kind}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t('tpm.pending.device')}{' '}
                    <Link to={`/tpm/devices/${p.deviceTpmId}`} className="text-theme-primary hover:underline">
                      {p.deviceLabel || p.deviceTpmId.slice(0, 8)}
                    </Link>
                    <span className="mx-1">•</span>
                    EK: <span className="font-mono">{p.deviceEk?.slice(0, 16)}…</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('tpm.pending.createdAt')} {formatDateTime(p.createdAt)} •
                    {' '}{t('tpm.pending.expiresAt')} {formatDateTime(p.expiresAt)}
                  </div>
                  {payload?.reason && (
                    <div className="text-sm mt-2 text-gray-700">
                      <span className="font-medium">{t('tpm.pending.reason')}</span> {payload.reason}
                    </div>
                  )}
                  {payload?.currentPcrs && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-gray-500">{t('tpm.pending.pcrCurrent')}</summary>
                      <pre className="mt-1 p-2 bg-gray-50 rounded font-mono overflow-x-auto">
                        {JSON.stringify(payload.currentPcrs, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={approve.isPending}
                    onClick={() => {
                      const note = prompt(t('tpm.pending.approvePrompt'), t('tpm.pending.approvePromptDefault'))
                      if (note !== null) approve.mutate({ id: p.id, note })
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {t('tpm.pending.approve')}
                  </button>
                  <button
                    disabled={deny.isPending}
                    onClick={() => {
                      const note = prompt(t('tpm.pending.denyPrompt'))
                      if (note) deny.mutate({ id: p.id, note })
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    {t('tpm.pending.deny')}
                  </button>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
